import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { Loading } from '@bumble/app/components/Loading';
import {
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';

interface ContactAssignEntityProps {
  contactId: string;
  onSubmit: () => void;
}

export const ContactAssignEntity = ({ contactId, onSubmit }: ContactAssignEntityProps) => {
  const api = useApi();

  const [entities, setEntities] = useState<string[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<string>('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);

  const { isLoading, data: entityNames } = useQuery(
    ['prepareEntityNames'],
    api.getEntityNamesApiV1GetEntityNamesGet.bind(null, {}),
    { enabled: true, cacheTime: 0, initialData: undefined },
  );

  const { isLoading: typesLoading, data: contactTypes } = useQuery(
    ['contacttypes'],
    api.contactTypesApiV1ContactTypesGet.bind(null, {}),
    { enabled: true },
  );

  useEffect(() => {
    if (entityNames != null && entityNames != undefined) {
      setEntities(entityNames.data || []);
      if (entityNames && Array.isArray(entityNames.data)) {
        setSelectedEntity(entityNames.data[0])
      }
    }
  }, [entityNames]);

  const assignEntity = useMutation(api.contactAssignEntityApiV1ContactAssignEntityPost, {
    onSuccess(data) {
      const msg = 'status' in data.data
        ? data.data.status as string
        : `Assigned entity successfully.`;
      toast.success(msg);
      onSubmit();
    },
    onError: apiError
  });

  const handleChange = ({ target: { value }}: SelectChangeEvent): void => {
    setSelectedEntity(value);
  };

  const handleChangeTypes = ({ target: { value }}: SelectChangeEvent) => {
    setSelectedTypes(typeof value === 'string' ? value.split(',') : value)
  };

  const handleSubmit = () => {
    if (selectedEntity && selectedTypes.length) {
      assignEntity.mutate({
        contact_id: contactId,
        contact_types: selectedTypes.join(','),
        new_entity_name: selectedEntity
      });
    } else {
      toast.error('Please select an entity and types.');
    }
  };

  if (isLoading || typesLoading) return <Loading />

  return (
    <Box pt={2}>
      <Box display="flex" justifyContent="center" sx={{ mb: 4 }}>
        <FormControl fullWidth>
          <InputLabel id="contact-types-label">Contact Types (Allow Multi Select)</InputLabel>
          <Select
            multiple
            labelId="contact-types-label"
            value={selectedTypes}
            label="Contact Types (Allow Multi Select)"
            onChange={handleChangeTypes}
          >
            { contactTypes?.data && contactTypes.data.map((item) => {
              return <MenuItem value={item} key={item}>{item}</MenuItem>;
            })}
          </Select>
        </FormControl>
      </Box>
      <Box display="flex" justifyContent="center" sx={{ mb: 4 }}>
        <FormControl fullWidth>
          <InputLabel id="entity-names-label">Entity Names</InputLabel>
          <Select
            labelId="entity-names-label"
            value={selectedEntity}
            label="Entity Names"
            onChange={handleChange}
          >
            { entities && entities.map((item) => {
              return <MenuItem value={item} key={item}>{item}</MenuItem>;
            })}
          </Select>
        </FormControl>
      </Box>
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="outlined" onClick={handleSubmit} disabled={!selectedEntity || !selectedTypes.length}>
          Assign
        </Button>
      </Box>
    </Box>
  );
};
